import { Card } from "@pericles/framework-frontend";
import { type ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  createFoundryV2RolloutState,
  fetchFoundryV2RouteResolution,
  type FoundryV2RouteResolutionRecord,
  type FoundryV2RouteResolutionTarget,
} from "../api/foundryV2Client";
import { Button } from "./ui";

interface FoundryV2AccessGuardProps {
  projectId: string;
  target: FoundryV2RouteResolutionTarget;
  isPending?: boolean;
  onActivated?: () => void;
  children: ReactNode;
}

function targetLabel(target: FoundryV2RouteResolutionTarget): string {
  if (target === "foundry-v2") return "Foundry V2";
  if (target === "governance-v2") return "Governance V2";
  return "Jarvis V2";
}

function parseEvidenceRefs(value: string): string[] {
  return Array.from(
    new Set(
      value
        .split(/[\n,;]/)
        .map((entry) => entry.trim())
        .filter(Boolean),
    ),
  );
}

export function FoundryV2AccessGuard({
  projectId,
  target,
  isPending = false,
  onActivated,
  children,
}: FoundryV2AccessGuardProps): JSX.Element {
  const navigate = useNavigate();
  const [resolution, setResolution] = useState<FoundryV2RouteResolutionRecord | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [operator, setOperator] = useState("release-board");
  const [evidenceRefs, setEvidenceRefs] = useState("JV2-BOOTSTRAP");
  const [note, setNote] = useState("");
  const [activationError, setActivationError] = useState<string | null>(null);
  const [isActivating, setIsActivating] = useState(false);

  useEffect(() => {
    if (!projectId.trim()) {
      setResolution(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    void fetchFoundryV2RouteResolution(projectId, target)
      .then((nextResolution) => {
        if (cancelled) return;
        setResolution(nextResolution);
        setActivationError(null);
      })
      .catch((cause) => {
        if (cancelled) return;
        setResolution(null);
        setError(cause instanceof Error ? cause.message : "Failed to confirm tenant rollout posture.");
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [projectId, target]);

  const canBootstrapFoundry =
    target === "foundry-v2" &&
    resolution?.recommendation.status === "redirect" &&
    resolution.featureFlagEnabled &&
    resolution.rolloutMode === "disabled";

  async function handleBootstrapFoundry(): Promise<void> {
    const decidedBy = operator.trim();
    const parsedEvidenceRefs = parseEvidenceRefs(evidenceRefs);
    if (!decidedBy) {
      setActivationError("Operator is required before activating Foundry V2.");
      return;
    }
    if (!parsedEvidenceRefs.length) {
      setActivationError("At least one evidence reference is required before activating Foundry V2.");
      return;
    }

    try {
      setIsActivating(true);
      setActivationError(null);
      await createFoundryV2RolloutState(projectId, {
        mode: "pilot",
        legacyFallbackEnabled: true,
        decidedBy,
        evidenceRefs: parsedEvidenceRefs,
        note: note.trim() || "Bootstrap Foundry V2 in pilot mode from the guarded access panel.",
      });
      const refreshedResolution = await fetchFoundryV2RouteResolution(projectId, target);
      setResolution(refreshedResolution);
      onActivated?.();
    } catch (cause) {
      setActivationError(
        cause instanceof Error ? cause.message : "Failed to activate Foundry V2 for this project.",
      );
    } finally {
      setIsActivating(false);
    }
  }

  if (isPending) {
    return (
      <div data-testid="foundry-access-guard-pending">
        <Card className="border border-slate-200" bodyClassName="space-y-2 p-6">
          <div className="text-sm font-semibold text-slate-900">Resolving Foundry access</div>
          <div className="text-sm text-slate-600">
            Confirming the project reference before opening the active delivery path.
          </div>
        </Card>
      </div>
    );
  }

  if (!projectId.trim()) {
    return <>{children}</>;
  }

  if (isLoading && !resolution) {
    return (
      <div data-testid="foundry-access-guard-loading">
        <Card className="border border-slate-200" bodyClassName="space-y-2 p-6">
          <div className="text-sm font-semibold text-slate-900">Checking tenant rollout posture</div>
          <div className="text-sm text-slate-600">
            Validating whether {targetLabel(target)} is the active delivery path for this project.
          </div>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="foundry-access-guard-error">
        <Card className="border border-amber-200" bodyClassName="space-y-3 p-6">
          <div className="text-sm font-semibold text-slate-900">Route confirmation failed</div>
          <div className="text-sm text-slate-700">{error}</div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => navigate(0)}>
              Retry
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (resolution?.recommendation.status === "redirect") {
    return (
      <div data-testid="foundry-access-guard">
        <Card className="border border-orange-200" bodyClassName="space-y-4 p-6">
          <div className="space-y-1">
            <div className="text-sm font-semibold text-slate-900" data-testid="foundry-access-guard-title">
              {targetLabel(target)} is not the active path
            </div>
            <div className="text-sm text-slate-700">{resolution.recommendation.reason}</div>
          </div>
          <div className="grid gap-2 text-xs text-slate-600 sm:grid-cols-2">
            <div>Rollout mode: {resolution.rolloutMode}</div>
            <div>Active path: {resolution.activePath}</div>
            <div>Feature enabled: {resolution.featureFlagEnabled ? "yes" : "no"}</div>
            <div>Legacy fallback: {resolution.routeAccess.legacyFallbackEnabled ? "yes" : "no"}</div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate(resolution.recommendation.route)}
            >
              Open legacy path
            </Button>
            <span className="text-xs text-slate-500">{resolution.recommendation.route}</span>
          </div>
          {canBootstrapFoundry ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="text-sm font-semibold text-slate-900">Bootstrap Foundry V2 access</div>
              <div className="mt-1 text-xs text-slate-600">
                Record a guarded pilot override to open the Foundry V2 lane for this project.
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                Defaults are prefilled for a fast bootstrap; adjust them before submitting if the operator of record is different.
              </div>
              <div className="mt-3 grid gap-3 text-xs text-slate-700">
                <label className="space-y-1">
                  <span className="font-semibold text-slate-800">Operator</span>
                  <input
                    className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                    type="text"
                    value={operator}
                    onChange={(event) => setOperator(event.target.value)}
                    placeholder="release-board"
                  />
                </label>
                <label className="space-y-1">
                  <span className="font-semibold text-slate-800">Evidence references</span>
                  <input
                    className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                    type="text"
                    value={evidenceRefs}
                    onChange={(event) => setEvidenceRefs(event.target.value)}
                    placeholder="CAB-99, JV2-BOOTSTRAP"
                  />
                </label>
                <label className="space-y-1">
                  <span className="font-semibold text-slate-800">Operator note</span>
                  <textarea
                    className="min-h-[72px] w-full rounded border border-slate-300 px-2 py-1 text-xs"
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    placeholder="Explain why Foundry V2 is being opened for this project now."
                  />
                </label>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  onClick={() => void handleBootstrapFoundry()}
                  disabled={isActivating}
                  data-testid="foundry-access-guard-activate"
                >
                  {isActivating ? "Activating..." : "Activate Foundry V2 (Pilot)"}
                </Button>
              </div>
              {activationError ? (
                <div className="mt-2 text-xs text-red-700" data-testid="foundry-access-guard-activation-error">
                  {activationError}
                </div>
              ) : null}
            </div>
          ) : null}
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}
